import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { LockOutlined, CrownOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import useFeatureAccess from '../hooks/useFeatureAccess';

/**
 * Component wrapper cho routes cần phân quyền feature
 * Hiển thị trang upgrade khi user không có quyền truy cập
 */
const FeatureRoute = ({ feature, children }) => {
  const { canAccessFeature, loading, initialized, subscription } = useFeatureAccess();

  // Chưa initialized hoặc đang loading -> hiển thị loading
  if (!initialized || loading) {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-h-screen tw-bg-gray-50 dark:tw-bg-fintech-navy">
        <div className="tw-text-center">
          <div className="tw-animate-spin tw-rounded-full tw-h-12 tw-w-12 tw-border-b-2 tw-border-blue-500 tw-mx-auto" />
          <p className="tw-mt-4 tw-text-gray-600 dark:tw-text-gray-400">Đang kiểm tra quyền truy cập...</p>
        </div>
      </div>
    );
  }

  // Có quyền -> render children
  if (canAccessFeature(feature)) {
    return children;
  }

  // Không có quyền -> hiển thị trang upgrade
  return (
    <div className="tw-min-h-screen tw-bg-gray-50 dark:tw-bg-fintech-navy tw-flex tw-items-center tw-justify-center tw-p-6">
      <div className="tw-max-w-lg tw-w-full tw-text-center">
        {/* Icon */}
        <div className="tw-mb-6">
          <div className="tw-inline-flex tw-items-center tw-justify-center tw-w-20 tw-h-20 tw-rounded-full tw-bg-gradient-to-br tw-from-amber-100 tw-to-orange-100 dark:tw-from-amber-900/30 dark:tw-to-orange-900/30">
            <LockOutlined className="tw-text-4xl tw-text-amber-600 dark:tw-text-amber-400" />
          </div>
        </div>

        {/* Title */}
        <h1 className="tw-text-2xl tw-font-bold tw-text-gray-900 dark:tw-text-white tw-mb-3">
          Tính năng bị khóa
        </h1>

        {/* Description */}
        <p className="tw-text-gray-600 dark:tw-text-gray-400 tw-mb-6">
          {subscription ? (
            <>
              Gói <span className="tw-font-semibold tw-text-gray-900 dark:tw-text-white">{subscription.subscription_name}</span> của bạn không bao gồm tính năng này.
              Nâng cấp để mở khóa đầy đủ tính năng!
            </>
          ) : (
            'Bạn cần đăng ký gói dịch vụ để sử dụng tính năng này.'
          )}
        </p>

        {/* Upgrade Card */}
        <div className="tw-bg-gradient-to-r tw-from-amber-50 tw-to-orange-50 dark:tw-from-amber-900/20 dark:tw-to-orange-900/20 tw-border tw-border-amber-200 dark:tw-border-amber-800 tw-rounded-xl tw-p-6 tw-mb-6">
          <CrownOutlined className="tw-text-3xl tw-text-amber-500 tw-mb-3" />
          <h3 className="tw-text-lg tw-font-semibold tw-text-gray-900 dark:tw-text-white tw-mb-2">
            Nâng cấp lên VIP
          </h3>
          <p className="tw-text-sm tw-text-gray-600 dark:tw-text-gray-400 tw-mb-4">
            Truy cập không giới hạn tất cả tính năng, phân tích chuyên sâu và hỗ trợ ưu tiên.
          </p>
          <Link
            to="/compare-plans"
            className="tw-inline-block tw-px-8 tw-py-3 tw-bg-gradient-to-r tw-from-amber-500 tw-to-orange-500 hover:tw-from-amber-600 hover:tw-to-orange-600 tw-text-white tw-font-semibold tw-rounded-lg tw-transition-all tw-shadow-lg hover:tw-shadow-xl"
          >
            Xem các gói dịch vụ
          </Link>
        </div>

        {/* Back link */}
        <Link
          to="/"
          className="tw-inline-flex tw-items-center tw-gap-2 tw-text-gray-600 dark:tw-text-gray-400 hover:tw-text-gray-900 dark:hover:tw-text-white tw-transition-colors"
        >
          <ArrowLeftOutlined />
          Quay lại trang chủ
        </Link>
      </div>
    </div>
  );
};

FeatureRoute.propTypes = {
  feature: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

export default FeatureRoute;

